import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCircleAlert,
  lucideCircleCheck,
  lucideCircleDashed,
  lucideTriangleAlert,
} from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import {
  BLACK,
  ContrastLevel,
  contrastLevel,
  contrastRatio,
  isHexColor,
  readableTextColor,
} from '../../../../../utils/color.utils';
import { formatWholeAmount } from '../../../../../utils/currency.utils';

interface LevelCopy {
  icon: string;
  title: string;
  description: string;
  tone: string;
}

const LEVEL_COPY: Record<ContrastLevel, LevelCopy> = {
  good: {
    icon: 'lucideCircleCheck',
    title: 'Gut lesbar',
    description: 'Der Text hebt sich deutlich von der Hauptfarbe ab.',
    tone: 'text-green-700 dark:text-green-500',
  },
  limited: {
    icon: 'lucideTriangleAlert',
    title: 'Eingeschränkt lesbar',
    description: 'Reicht für Buttons, kleiner Text ist schwer lesbar.',
    tone: 'text-warning',
  },
  poor: {
    icon: 'lucideCircleAlert',
    title: 'Schlecht lesbar',
    description: 'Kaum zu erkennen, so lässt sich nicht speichern.',
    tone: 'text-destructive',
  },
};

/** Shown while a colour is being typed and is not a full #RRGGBB yet. */
const PENDING_COPY: LevelCopy = {
  icon: 'lucideCircleDashed',
  title: 'Kontrast wird geprüft',
  description: 'Sobald beide Farben gültig sind, siehst du hier die Lesbarkeit.',
  tone: 'text-muted-foreground',
};

/**
 * Shows how the text colour reads on the primary colour: a live preview in both
 * colours, the WCAG contrast ratio with a verdict, and — when it is not good —
 * black or white as a one-click fix.
 */
@Component({
  selector: 'app-color-contrast-check',
  imports: [NgIcon, HlmButtonImports],
  providers: [
    provideIcons({ lucideCircleAlert, lucideCircleCheck, lucideCircleDashed, lucideTriangleAlert }),
  ],
  templateUrl: './color-contrast-check.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorContrastCheckComponent {
  readonly background = input.required<string>();
  readonly text = input.required<string>();

  /** The user took the suggested text colour. */
  readonly applySuggestion = output<string>();

  /** Both colours are valid #RRGGBB values — otherwise there is nothing to judge. */
  protected readonly valid = computed(
    () => isHexColor(this.background()) && isHexColor(this.text()),
  );

  protected readonly ratio = computed(() =>
    this.valid() ? contrastRatio(this.background(), this.text()) : 0,
  );

  protected readonly level = computed(() => contrastLevel(this.ratio()));
  protected readonly copy = computed(() =>
    this.valid() ? LEVEL_COPY[this.level()] : PENDING_COPY,
  );
  /** The preview's sample text, in the shop's currency: "10 €", "CHF 10" */
  protected readonly fromAmount = computed(() => formatWholeAmount(1000));

  /** The fix is offered only when the contrast is not good; its space is always kept. */
  protected readonly showSuggestion = computed(() => this.valid() && this.level() !== 'good');

  /**
   * Black or white always reaches at least 4.5 : 1, so it fixes every other level.
   * Computed for any valid background, so the (hidden) button keeps its size.
   */
  protected readonly suggestion = computed(() => {
    const background = isHexColor(this.background()) ? this.background() : BLACK;
    const color = readableTextColor(background);
    return {
      color,
      label: color === BLACK ? 'Schwarz' : 'Weiß',
      ratio: contrastRatio(background, color),
    };
  });

  protected formatRatio(ratio: number): string {
    return `${ratio.toLocaleString('de-DE', { maximumFractionDigits: 1, minimumFractionDigits: 1 })} : 1`;
  }
}
